import { useEffect, useState } from "react";

import dayjs from "dayjs";
import isEmpty from "lodash-es/isEmpty";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import NumericInput from "react-numeric-input";
import LoadingButton from "@mui/lab/LoadingButton";

import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Grid from "@mui/material/Grid";

import { doService } from "../../services/ServiceHelper";
import {
  getCommServiceStatusText,
  getCommServiceComments,
  adminGetCommServiceDownloadUrl,
  downloadFile,
  adminApproveCommService,
  adminRejectCommService,
  deleteCommService,
  transferCommServiceOp,
  commentCommServiceOp,
  removeCommServiceComment,
  getCommServiceReminders,
  sendStudentReminder,
} from "../../services/CommServiceService";
import { getSchoolTeachers } from "../../services/SchoolService";
import {
  StripedTableRow,
  StripedTableCell,
  StripedTableHeadCell,
} from "../StripedTable";
import { showOptions, showToast } from "../ATPDialogs";
import { getUserPriority } from "../../services/UserService";
import { TableHead } from "@mui/material";
import TableRowNoResults from "../../components/TableRowNoResults";
import InlineEditableText from "../../components/InlineEditableText";
import InlineEditableSelect from "../InlineEditableSelect";
import { getCommServiceTypes } from "../../services/CommServiceService";

const CommServiceDetailsDialog = ({
  school,
  jwt,
  dialogOpen,
  setDialogOpen,
  dialogClose,
  selectedItem,
  doUpdateSelectedItem,
  onApprove,
  onReject,
  onDelete,
  isReviewer = false,
  onChange = null,
}) => {
  const [_teachers, setTeachers] = useState([]);
  const _priority = getUserPriority(jwt);
  const [_comments, setComments] = useState([]);
  const [_reminders, setReminders] = useState([]);
  const [_buttonProcessing, setButtonProcessing] = useState(false);
  const [_serviceTypes, setServiceTypes] = useState([]);

  useEffect(() => {
    initTeachers();
    initTypes();
  }, []);

  const handleClose = () => {
    setDialogOpen(false);
    if (dialogClose) {
      dialogClose();
    }
  };

  const initTypes = () => {
    doService(getCommServiceTypes(school, jwt), (result) => {
      // remove types not available to students
      const types = [];
      for (const t of result.data) {
        types.push({
          label: t.name,
          value: t.eid,
        });
      }

      setServiceTypes(types);
    });
  };

  const initTeachers = () => {
    doService(getSchoolTeachers(school, jwt), (result) => {
      setTeachers(result.data);
    });
  };

  const initReminders = (opEid) => {
    doService(getCommServiceReminders(school, jwt, opEid), (result) => {
      setReminders(result.data);
    });
  };

  const onClickDownloadFile = () => {
    doService(
      adminGetCommServiceDownloadUrl(school, jwt, selectedItem.eid),
      (result) => {
        downloadFile(selectedItem.originalFilename, result.data);
      },
    );
  };

  const handleApprove = () => {
    doService(
      adminApproveCommService(
        school,
        jwt,
        selectedItem.eid,
        selectedItem.hourNum,
        selectedItem.minuteNum,
      ),
      (result) => {
        showToast("S", "Service Approved.");
        setDialogOpen(false);
        if (onApprove) {
          onApprove();
        }
      },
    );
  };

  const handleReject = () => {
    const reason = window.prompt("Please enter a Rejected Reason.");
    if (isEmpty(reason)) {
      return;
    }

    doService(
      adminRejectCommService(
        school,
        jwt,
        selectedItem.eid,
        reason,
        selectedItem.hourNum,
        selectedItem.minuteNum,
      ),
      (result) => {
        showToast("S", "Service Rejected.");
        setDialogOpen(false);
        if (onReject) {
          onReject();
        }
      },
    );
  };

  const handleDelete = () => {
    if (window.confirm("Click OK to Delete this Service Entry.")) {
      doService(deleteCommService(school, jwt, selectedItem.eid), (result) => {
        showToast("S", "Service Deleted.");
        setDialogOpen(false);
        if (onDelete) {
          onDelete();
        }
      });
    }
  };

  const getTeacherOptions = () => {
    const map = {};
    for (let teacher of _teachers) {
      map[teacher.eid] = `${teacher.last_name}, ${teacher.first_name}`;
    }
    return map;
  };

  const handleTransfer = async () => {
    setDialogOpen(false);
    const transferResult = await showOptions(
      `Transfer Service Op for ${selectedItem.studentFirstName} ${selectedItem.studentLastName} to Another Teacher`,
      "Click Teacher Name",
      getTeacherOptions(),
    );
    if (!transferResult.isConfirmed) {
      return;
    }

    doService(
      transferCommServiceOp(
        school,
        jwt,
        selectedItem.eid,
        transferResult.value,
      ),
      (result) => {
        showToast("S", "Service Approved.");
        setDialogOpen(false);
        if (onApprove) {
          onApprove();
        }
      },
    );
  };

  const handleAddComment = () => {
    const comment = window.prompt("Please enter comment.");
    if (isEmpty(comment)) {
      return;
    }

    doService(
      commentCommServiceOp(school, jwt, selectedItem.eid, comment),
      (result) => {
        showToast("S", "Comment Added.");
        initComments(selectedItem.eid);
      },
    );
  };

  const initComments = (opEid) => {
    if (opEid == null) {
      return;
    }
    doService(getCommServiceComments(school, jwt, opEid), (result) => {
      setComments(result.data);
    });
  };

  const handleSendReminder = () => {
    setButtonProcessing(true);
    doService(sendStudentReminder(school, jwt, selectedItem.eid), (results) => {
      showToast("S", "Email(s) Sent");
      initReminders(selectedItem.eid);
      setButtonProcessing(false);
    });
  };

  const onClickDeleteComment = (commentEid) => {
    if (!window.confirm(`Click OK to remove comment.`)) {
      return;
    }
    doService(removeCommServiceComment(school, jwt, commentEid), (result) => {
      initComments(selectedItem.eid);
      showToast("S", "Comment Removed!");
    });
  };

  const updateType = (newTypeEid) => {
    if (onChange) {
      onChange("type", newTypeEid);
    }
  };

  const updateDescription = (newText) => {
    if (onChange) {
      onChange("description", newText);
    }
  };

  const getTypeDisplayText = () => {
    const selectedTypeEid = selectedItem?.typeEid;
    if (selectedTypeEid == null) {
      return selectedItem.typeName + " (deleted)";
    }

    const matchedType = _serviceTypes.find(
      (type) => type.value === selectedTypeEid,
    );

    return matchedType
      ? matchedType.label.replace(/\/$/, "")
      : selectedItem.typeName + " (deleted)";
  };

  return (
    <Dialog
      onClose={handleClose}
      open={dialogOpen}
      fullWidth={true}
      fullScreen
      TransitionProps={{
        onEntered: () => {
          initComments(selectedItem.eid);
          initReminders(selectedItem.eid);
        },
      }}
    >
      <DialogTitle>
        Service Op for {selectedItem.studentFirstName}{" "}
        {selectedItem.studentLastName} ({selectedItem.grade})
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
              <AccordionSummary className="accordionTitle">
                Op Details
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 0 }}>
                  <Table sx={{ width: "100%" }} size="small">
                    <TableBody>
                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Service Date (Range)
                        </StripedTableHeadCell>
                        <StripedTableCell>
                          {selectedItem.eventDateStart !==
                            selectedItem.eventDateEnd && (
                            <>
                              {dayjs(selectedItem.eventDateStart).format(
                                "MM/DD/YY",
                              )}{" "}
                              -{" "}
                              {dayjs(selectedItem.eventDateEnd).format(
                                "MM/DD/YY",
                              )}
                            </>
                          )}
                          {selectedItem.eventDateStart ===
                            selectedItem.eventDateEnd && (
                            <>
                              {dayjs(selectedItem.eventDateStart).format(
                                "MM/DD/YY",
                              )}
                            </>
                          )}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Created</StripedTableHeadCell>
                        <StripedTableCell>
                          {dayjs(selectedItem.dateCreated).format(
                            "MM/DD/YY hh:mm a",
                          )}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Type</StripedTableHeadCell>
                        <StripedTableCell>
                          {getTypeDisplayText()}
                          <InlineEditableSelect
                            value={selectedItem.typeEid}
                            options={_serviceTypes}
                            onChange={(value) => updateType(value)}
                          />
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Description</StripedTableHeadCell>
                        <StripedTableCell>
                          <InlineEditableText
                            value={selectedItem.description}
                            onChange={(value) => updateDescription(value)}
                          />
                        </StripedTableCell>
                      </StripedTableRow>
                      {selectedItem.teacherEid != null && (
                        <>
                          <StripedTableRow>
                            <StripedTableHeadCell>
                              Created By
                            </StripedTableHeadCell>
                            <StripedTableCell>
                              {selectedItem.teacherLastName},{" "}
                              {selectedItem.teacherFirstName}
                            </StripedTableCell>
                          </StripedTableRow>
                        </>
                      )}
                      <StripedTableRow>
                        <StripedTableHeadCell>Reviewed By</StripedTableHeadCell>
                        <StripedTableCell>
                          {selectedItem.reviewerLastName == null ? (
                            <>-- NOT REVIEWED --</>
                          ) : (
                            <>
                              {selectedItem.reviewerLastName},{" "}
                              {selectedItem.reviewerFirstName}
                            </>
                          )}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Status</StripedTableHeadCell>
                        <StripedTableCell>
                          {getCommServiceStatusText(selectedItem.status)}
                        </StripedTableCell>
                      </StripedTableRow>
                      {selectedItem.status === -100 && (
                        <>
                          <StripedTableRow>
                            <StripedTableHeadCell>
                              Rejected By
                            </StripedTableHeadCell>
                            <StripedTableCell>
                              {selectedItem.rejectedByLastName},{" "}
                              {selectedItem.rejectedByFirstName}
                            </StripedTableCell>
                          </StripedTableRow>
                          <StripedTableRow>
                            <StripedTableHeadCell>
                              Rejected Reason
                            </StripedTableHeadCell>
                            <StripedTableCell>
                              {selectedItem.rejectReason}
                            </StripedTableCell>
                          </StripedTableRow>
                        </>
                      )}
                      {selectedItem.adminApproverLastName != null && (
                        <StripedTableRow>
                          <StripedTableHeadCell>
                            Admin Approver
                          </StripedTableHeadCell>
                          <StripedTableCell>
                            {selectedItem.adminApproverLastName},{" "}
                            {selectedItem.adminApproverFirstName}
                          </StripedTableCell>
                        </StripedTableRow>
                      )}
                      <StripedTableRow>
                        <StripedTableHeadCell>Hours</StripedTableHeadCell>
                        <StripedTableCell>
                          <NumericInput
                            className="numericInput"
                            min={0}
                            max={40}
                            value={selectedItem.hourNum}
                            onChange={(value) => {
                              doUpdateSelectedItem(
                                value,
                                selectedItem.minuteNum,
                              );
                            }}
                          />
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>Minutes</StripedTableHeadCell>
                        <StripedTableCell>
                          <NumericInput
                            className="numericInput"
                            min={0}
                            max={45}
                            step={15}
                            value={selectedItem.minuteNum}
                            onChange={(value) => {
                              doUpdateSelectedItem(selectedItem.hourNum, value);
                            }}
                          />
                        </StripedTableCell>
                      </StripedTableRow>
                      {(selectedItem.verifyWith === "ALL" ||
                        selectedItem.verifyWith === "FI") &&
                        isEmpty(selectedItem.originalFilename) === false && (
                          <StripedTableRow>
                            <StripedTableHeadCell>
                              Document
                            </StripedTableHeadCell>
                            <StripedTableCell>
                              <Link
                                component="button"
                                onClick={onClickDownloadFile}
                              >
                                {selectedItem.originalFilename}
                              </Link>
                            </StripedTableCell>
                          </StripedTableRow>
                        )}

                      {isEmpty(selectedItem.teacherEid) === false && (
                        <StripedTableRow>
                          <StripedTableHeadCell>
                            Created by Teacher
                          </StripedTableHeadCell>
                          <StripedTableCell>
                            {selectedItem.teacherLastName},{" "}
                            {selectedItem.teacherFirstName}
                          </StripedTableCell>
                        </StripedTableRow>
                      )}

                      {(selectedItem.verifyWith === "ALL" ||
                        selectedItem.verifyWith === "VE") &&
                        isEmpty(selectedItem.verifierEmail) === false && (
                          <StripedTableRow>
                            <StripedTableHeadCell>
                              Verifier Email
                            </StripedTableHeadCell>
                            <StripedTableCell>
                              {selectedItem.verifierEmail}
                            </StripedTableCell>
                          </StripedTableRow>
                        )}

                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Verifier Status
                        </StripedTableHeadCell>
                        <StripedTableCell>
                          {selectedItem.verifierStatus === 0 && (
                            <Chip label="PENDING" color="primary" />
                          )}
                          {selectedItem.verifierStatus === -100 && (
                            <>
                              <Chip label="REJECTED" color="error" /> (
                              {selectedItem.verifierReason})
                            </>
                          )}
                          {selectedItem.verifierStatus === 100 && (
                            <>
                              <Chip label="APPROVED" color="success" /> (
                              {dayjs(selectedItem.dateVerified).format(
                                "MM/DD/YY",
                              )}
                              )
                            </>
                          )}
                        </StripedTableCell>
                      </StripedTableRow>
                      {selectedItem.deletedBy != null && (
                        <StripedTableRow>
                          <StripedTableHeadCell>
                            Deleted By
                          </StripedTableHeadCell>
                          <StripedTableCell>
                            {selectedItem.deletedByLastName},{" "}
                            {selectedItem.deletedByFirstName}
                          </StripedTableCell>
                        </StripedTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {(isReviewer === true ||
              _priority === 525 ||
              _priority === 800) && (
              <>
                <Button
                  onClick={handleApprove}
                  variant="outlined"
                  className="buttonGreen"
                  sx={{ width: "180px", mt: 2, ml: 2 }}
                >
                  Approve
                </Button>
                <Button
                  onClick={handleReject}
                  variant="outlined"
                  className="buttonRed"
                  sx={{ width: "180px", ml: 2, mt: 2 }}
                >
                  Reject
                </Button>
                <br />
                {_priority !== 525 && (
                  <Button
                    onClick={handleTransfer}
                    variant="contained"
                    sx={{ width: "180px", mt: 2, ml: 2 }}
                  >
                    Transfer
                  </Button>
                )}
                <Button
                  onClick={handleDelete}
                  variant="outlined"
                  sx={{ width: "180px", ml: 2, mt: 2 }}
                >
                  Delete
                </Button>
              </>
            )}
          </Grid>
          <Grid item xs={6}>
            <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
              <AccordionSummary className="accordionTitle">
                Comments
              </AccordionSummary>
              <AccordionDetails>
                <Button
                  onClick={handleAddComment}
                  variant="contained"
                  size="small"
                  sx={{ width: "200px", mt: 0, mb: 1 }}
                >
                  Add Comment
                </Button>

                <TableContainer component={Paper} sx={{ mt: 0 }}>
                  <Table sx={{ width: "100%" }} size="small">
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell align="center" width={110}>
                          Date
                        </StripedTableCell>
                        <StripedTableCell>Who</StripedTableCell>
                        <StripedTableCell>Comment</StripedTableCell>
                        <StripedTableCell align="center">
                          Delete?
                        </StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults
                        data={_comments}
                        text="-- NO COMMENTS RETURNED --"
                      />
                      {_comments.map((commentObj) => (
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            {dayjs(commentObj.dateCreated).format(
                              "MM/DD/YY hh:mm a",
                            )}
                          </StripedTableCell>
                          <StripedTableCell>
                            {commentObj.last_name}, {commentObj.first_name}
                          </StripedTableCell>
                          <StripedTableCell>
                            {commentObj.comment}
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            <Button
                              variant="outlined"
                              onClick={() =>
                                onClickDeleteComment(commentObj.eid)
                              }
                            >
                              Delete
                            </Button>
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters sx={{ boxShadow: 1, mt: 2 }} expanded>
              <AccordionSummary className="accordionTitle">
                Reminder History
              </AccordionSummary>
              <AccordionDetails>
                <LoadingButton
                  sx={{ width: "200px", mt: 0, mb: 1 }}
                  size="small"
                  onClick={handleSendReminder}
                  loading={_buttonProcessing}
                  loadingPosition="end"
                  variant="contained"
                >
                  <span>Send Reminder</span>
                </LoadingButton>

                <TableContainer component={Paper} sx={{ mt: 0 }}>
                  <Table sx={{ width: "100%" }} size="small">
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell align="center" width={110}>
                          Date
                        </StripedTableCell>
                        <StripedTableCell>Sender</StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults
                        data={_reminders}
                        text="-- NO REMINDERS RETURNED --"
                      />
                      {_reminders.map((reminder) => (
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            {dayjs(reminder.dateCreated).format(
                              "MM/DD/YY hh:mm a",
                            )}
                          </StripedTableCell>
                          <StripedTableCell>
                            {reminder.last_name}, {reminder.first_name}
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommServiceDetailsDialog;
